import { AuthSession } from 'aws-amplify/auth';
import {
  PipelineInspectorResponse,
  PipelineCost,
  TimeframePeriod,
  ServiceCostBreakdown,
} from '../types';
import { cacheService } from './cacheService';

// API Configuration
// Detect environment based on the hostname where the web app is running
const API_BASE_URL = window.location.hostname.includes('gamma')
  ? 'https://api.gamma.catalyst.step.amazon.dev'
  : 'https://api.catalyst.step.amazon.dev';

// For local development, default to gamma
const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'https://api.gamma.catalyst.step.amazon.dev';
  }
  return API_BASE_URL;
};

const FINAL_API_BASE_URL = getApiBaseUrl();

// Types for API responses
export interface ApiPipeline {
  name: string;
  filename: string; // Add filename property for uniqueness checking
  description?: string;
  status: string;
  lastModified: string;
  recordsOut: number;
  tps: number;
  tags?: string[];
  sns_sources?: string[];
  destination_tables?: string[];
  components?: any[];
  stage?: string; // Add stage property for environment filtering
  enabled?: boolean; // Add enabled property for status mapping
  // Usage metrics data
  pipeline_metrics?: {
    total_records_written?: number;
    records_per_second?: number;
    total_records_processed?: number;
    timeframe?: string;
    components_analyzed?: number;
    storage_components?: any[];
    processing_components?: any[];
  };
  // Component usage data
  nodes?: any[];
  edges?: any[];
  // Cost summary data
  pipeline_cost_summary?: {
    [year: string]: {
      total_weekly_cost?: number;
      total_monthly_cost?: number;
      total_annual_cost?: number;
      component_count?: number;
      components_with_costs?: number;
    };
  };
}

export interface ListPipelinesResponse {
  pipelines: ApiPipeline[];
  totalCount: number;
}

export interface GetPipelineResponse {
  pipeline: ApiPipeline;
}

/**
 * API service for interacting with the Catalyst API Gateway
 * Enhanced with intelligent caching for optimal performance
 */
class ApiService {
  private currentEnvironment: string | null = null;

  /**
   * Detect current environment based on API URL
   */
  private detectEnvironment(): string {
    const apiUrl = getApiBaseUrl();
    return apiUrl.includes('gamma') ? 'gamma' : 'prod';
  }

  /**
   * Handle environment changes and track current environment
   */
  private handleEnvironmentChange(newEnvironment: string): void {
    if (this.currentEnvironment && this.currentEnvironment !== newEnvironment) {
      console.log(`Environment changed from ${this.currentEnvironment} to ${newEnvironment}`);
      // Don't clear cache - let each environment maintain its own cache naturally
    }
    this.currentEnvironment = newEnvironment;
  }

  /**
   * Get current environment (public method for external cache clearing)
   */
  getCurrentEnvironment(): string {
    return this.currentEnvironment || this.detectEnvironment();
  }
  private getAuthHeaders(session: AuthSession | null): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (session?.tokens?.idToken) {
      // Cognito User Pool authorizer expects the JWT token directly, not prefixed with "Bearer"
      headers['Authorization'] = session.tokens.idToken.toString();
      console.log(
        'Sending Authorization header (JWT token):',
        session.tokens.idToken.toString().substring(0, 50) + '...',
      );
    } else {
      console.warn('No idToken available for authentication');
    }

    return headers;
  }

  private async makeRequest<T>(
    endpoint: string,
    session: AuthSession | null,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${FINAL_API_BASE_URL}${endpoint}`;
    console.log(`Making API request to: ${url}`); // Debug logging to verify correct URL

    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(session),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `API request failed: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  /**
   * List all available pipelines
   */
  async listPipelines(
    session: AuthSession | null,
    forceRefresh: boolean = false,
    uiEnvironment?: string,
  ): Promise<ListPipelinesResponse> {
    // Use UI environment if provided, otherwise detect from URL
    const environment = uiEnvironment || this.detectEnvironment();
    this.handleEnvironmentChange(environment);

    // Use environment-specific cache key to avoid cross-environment contamination
    const cacheKey = `${environment}_pipelines`;

    // Check cache first (unless force refresh is requested)
    if (!forceRefresh) {
      const cachedResponse = cacheService.get<ListPipelinesResponse>(
        environment,
        'all', // Use 'all' as timeframe for list pipelines since it doesn't depend on timeframe
        'list_pipelines',
      );

      if (cachedResponse) {
        console.log(
          `Cache HIT for ${cacheKey}: Found ${cachedResponse.pipelines.length} pipelines`,
        );
        return cachedResponse;
      }
    } else {
      console.log(`Force refresh requested for ${cacheKey}, clearing cache`);
      // Clear environment-specific cache on force refresh
      this.clearEnvironmentCache();
    }

    try {
      console.log(`Making API request for ${cacheKey}...`);
      const response = await this.makeRequest<any>('/pipelines', session, {
        method: 'POST',
        body: JSON.stringify({
          action: 'list_pipelines',
        }),
      });

      // Transform the response to match our expected format
      let result: ListPipelinesResponse;

      if (response.pipelines && Array.isArray(response.pipelines)) {
        result = {
          pipelines: response.pipelines.map(this.transformPipelineData),
          totalCount: response.pipelines.length,
        };
      } else if (Array.isArray(response)) {
        result = {
          pipelines: response.map(this.transformPipelineData),
          totalCount: response.length,
        };
      } else {
        // Fallback for unexpected response format
        console.warn('Unexpected API response format:', response);
        result = {
          pipelines: [],
          totalCount: 0,
        };
      }

      console.log(`API response for ${cacheKey}: Found ${result.pipelines.length} pipelines`);

      // Cache the successful response for 3 minutes (reduced from 5 to ensure fresher data)
      cacheService.set(
        environment,
        'all',
        'list_pipelines',
        result,
        undefined,
        3 * 60 * 1000, // 3 minutes TTL
      );

      console.log(`Cache SET for ${cacheKey}: Cached ${result.pipelines.length} pipelines`);
      return result;
    } catch (error) {
      console.error(`Error listing pipelines for ${cacheKey}:`, error);
      throw error;
    }
  }

  /**
   * Get details for a specific pipeline
   */
  async getPipeline(
    pipelineName: string,
    session: AuthSession | null,
    timeframe: string = 'daily',
    forceRefresh: boolean = false,
  ): Promise<GetPipelineResponse> {
    const environment = this.detectEnvironment();
    this.handleEnvironmentChange(environment);

    // Check cache first (unless force refresh is requested)
    if (!forceRefresh) {
      const cachedResponse = cacheService.get<GetPipelineResponse>(
        environment,
        timeframe,
        'get_pipeline',
        { pipeline_name: pipelineName },
      );

      if (cachedResponse) {
        return cachedResponse;
      }
    }
    try {
      const response = await this.makeRequest<any>('/pipelines', session, {
        method: 'POST',
        body: JSON.stringify({
          action: 'get_pipeline',
          pipeline_name: pipelineName,
          include_usage_metrics: true, // Request usage metrics for charts
          timeframe: timeframe, // Use the provided timeframe
        }),
      });

      console.log('Raw API response:', response);

      const result: GetPipelineResponse = {
        pipeline: this.transformPipelineData(response.pipeline || response),
      };

      // Cache the successful response for 3 minutes (pipeline details change more frequently)
      cacheService.set(
        environment,
        timeframe,
        'get_pipeline',
        result,
        { pipeline_name: pipelineName },
        3 * 60 * 1000, // 3 minutes TTL
      );

      return result;
    } catch (error) {
      console.error(`Error getting pipeline ${pipelineName}:`, error);
      throw error;
    }
  }

  /**
   * Transform API pipeline data to match frontend interface
   */
  private transformPipelineData(apiPipeline: any): ApiPipeline {
    // Convert enabled boolean to status string
    let status = 'unknown';
    if (typeof apiPipeline.enabled === 'boolean') {
      status = apiPipeline.enabled ? 'healthy' : 'disabled';
    } else if (apiPipeline.status) {
      status = apiPipeline.status;
    }

    // Convert tags object to array of strings
    let tags: string[] = [];
    if (apiPipeline.tags && typeof apiPipeline.tags === 'object') {
      tags = Object.entries(apiPipeline.tags).map(([key, value]) => `${key}: ${value}`);
    } else if (Array.isArray(apiPipeline.tags)) {
      tags = apiPipeline.tags;
    }

    // Extract records and TPS from pipeline_metrics if available (from get_pipeline response)
    const pipelineMetrics = apiPipeline.pipeline_metrics;
    const recordsOut =
      apiPipeline.recordsOut ||
      apiPipeline.records_out ||
      pipelineMetrics?.total_records_written ||
      0;
    const tps =
      apiPipeline.tps || apiPipeline.throughput || pipelineMetrics?.records_per_second || 0;

    return {
      name: apiPipeline.name || apiPipeline.pipeline_name || 'Unknown Pipeline',
      filename: apiPipeline.filename || 'unknown-filename', // Include filename for uniqueness
      description: apiPipeline.description || apiPipeline.pipeline_description || '',
      status: status,
      lastModified:
        apiPipeline.lastModified || apiPipeline.last_modified || new Date().toISOString(),
      recordsOut: recordsOut,
      tps: tps,
      tags: tags,
      sns_sources: apiPipeline.sns_sources || [],
      destination_tables: apiPipeline.destination_tables || [],
      components: apiPipeline.components || [],
      stage: apiPipeline.stage || 'unknown', // Include stage for environment filtering
      // Preserve usage metrics data for charts
      pipeline_metrics: pipelineMetrics,
      nodes: apiPipeline.nodes,
      edges: apiPipeline.edges,
      // Preserve cost summary data for charts
      pipeline_cost_summary: apiPipeline.pipeline_cost_summary,
    };
  }

  /**
   * Get pipeline cost data with specified timeframe
   */
  async getPipelineCost(
    pipelineName: string,
    timeframe: TimeframePeriod,
    session: AuthSession | null,
    costYear: number = 2025,
    forceRefresh: boolean = false,
  ): Promise<PipelineCost> {
    const environment = this.detectEnvironment();
    this.handleEnvironmentChange(environment);

    // Check cache first (unless force refresh is requested)
    if (!forceRefresh) {
      const cachedResponse = cacheService.get<PipelineCost>(
        environment,
        timeframe,
        'get_pipeline_cost',
        { pipeline_name: pipelineName, cost_year: costYear },
      );

      if (cachedResponse) {
        return cachedResponse;
      }
    }
    try {
      const response = await this.makeRequest<PipelineInspectorResponse>('/pipelines', session, {
        method: 'POST',
        body: JSON.stringify({
          action: 'get_pipeline',
          pipeline_name: pipelineName,
          timeframe: timeframe,
          include_usage_metrics: true, // Need usage metrics for accurate records/TPS per timeframe
          include_cost_metrics: true,
          cost_year: costYear,
        }),
      });

      // Extract cost summary for the specified year
      const costSummary = response.pipeline_cost_summary?.[costYear.toString() as '2025' | '2026'];
      const pipelineMetrics = response.pipeline_metrics;

      let result: PipelineCost;

      if (costSummary) {
        result = {
          weekly_cost: costSummary.total_weekly_cost || 0,
          monthly_cost: costSummary.total_monthly_cost || 0,
          annual_cost: costSummary.total_annual_cost || 0,
          currency: 'USD',
          // Include pipeline metrics for records and TPS
          records_written: pipelineMetrics?.total_records_written || 0,
          records_per_second: pipelineMetrics?.records_per_second || 0,
        };
      } else {
        // Fallback if no cost data available
        result = {
          weekly_cost: 0,
          monthly_cost: 0,
          annual_cost: 0,
          currency: 'USD',
          error: 'No cost data available',
          records_written: pipelineMetrics?.total_records_written || 0,
          records_per_second: pipelineMetrics?.records_per_second || 0,
        };
      }

      // Cache the successful response for 10 minutes (cost data changes less frequently)
      cacheService.set(
        environment,
        timeframe,
        'get_pipeline_cost',
        result,
        { pipeline_name: pipelineName, cost_year: costYear },
        10 * 60 * 1000, // 10 minutes TTL
      );

      return result;
    } catch (error) {
      console.error(`Error getting pipeline cost for ${pipelineName}:`, error);
      const errorResult: PipelineCost = {
        weekly_cost: 0,
        monthly_cost: 0,
        annual_cost: 0,
        currency: 'USD',
        error: error instanceof Error ? error.message : 'Unknown error',
      };

      // Don't cache error responses
      return errorResult;
    }
  }

  /**
   * Health check endpoint (if available)
   */
  async healthCheck(
    session: AuthSession | null,
    forceRefresh: boolean = false,
  ): Promise<{ status: string }> {
    const environment = this.detectEnvironment();

    // Check cache first (unless force refresh is requested)
    if (!forceRefresh) {
      const cachedResponse = cacheService.get<{ status: string }>(
        environment,
        'all',
        'health_check',
      );

      if (cachedResponse) {
        return cachedResponse;
      }
    }
    try {
      const response = await this.makeRequest<{ status: string }>('/health', session);

      // Cache health check for 1 minute
      cacheService.set(
        environment,
        'all',
        'health_check',
        response,
        undefined,
        60 * 1000, // 1 minute TTL
      );

      return response;
    } catch (error) {
      console.warn('Health check failed:', error);
      const errorResult = { status: 'unknown' };

      // Don't cache error responses for health check
      return errorResult;
    }
  }

  /**
   * Clear cache for a specific pipeline (useful when pipeline is updated)
   */
  clearPipelineCache(pipelineName: string): void {
    cacheService.clearPipeline(pipelineName);
  }

  /**
   * Clear all cache entries for current environment
   */
  clearEnvironmentCache(): void {
    const environment = this.detectEnvironment();
    cacheService.clearEnvironment(environment);
  }

  /**
   * Clear cache for specific environment and timeframe
   */
  clearEnvironmentTimeframeCache(timeframe: string): void {
    const environment = this.detectEnvironment();
    cacheService.clearEnvironmentTimeframe(environment, timeframe);
  }

  /**
   * Clear all cached data
   */
  clearAllCache(): void {
    cacheService.clearAll();
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    return cacheService.getStats();
  }

  /**
   * Get aggregated cost data by service category from already loaded pipeline data
   * Uses the exact same cost calculation as the pipelines table for consistency
   */
  async getAggregatedCostsFromPipelines(
    pipelines: ApiPipeline[],
    timeframe: TimeframePeriod = 'weekly',
    costYear: number = 2025,
  ): Promise<{ serviceBreakdown: ServiceCostBreakdown[]; totalCost: number }> {
    const serviceBreakdown = new Map<string, number>();
    let totalPipelineCost = 0;

    // For each pipeline that has cost data already loaded, break it down by service
    for (const pipeline of pipelines) {
      if (pipeline.status === 'disabled') continue;

      // Check if this pipeline already has cost data
      const costSummary = pipeline.pipeline_cost_summary?.[costYear.toString()];
      if (costSummary && costSummary.total_weekly_cost && costSummary.total_weekly_cost > 0) {
        const pipelineCost = costSummary.total_weekly_cost;
        totalPipelineCost += pipelineCost;

        // Break down this pipeline's cost by service category using node data
        if (pipeline.nodes) {
          const pipelineTotalFromNodes = pipeline.nodes.reduce((sum, node) => {
            const costMetrics = node.cost_metrics?.[costYear.toString()];
            return sum + (costMetrics?.weekly_cost || 0);
          }, 0);

          if (pipelineTotalFromNodes > 0) {
            for (const node of pipeline.nodes) {
              const costMetrics = node.cost_metrics?.[costYear.toString()];
              if (costMetrics && !costMetrics.error && costMetrics.weekly_cost > 0) {
                const serviceCategory = this.mapComponentTypeToServiceCategory(node.type);

                // Use exact node cost (not proportional calculation)
                const nodeCost = costMetrics.weekly_cost;

                const currentCost = serviceBreakdown.get(serviceCategory) || 0;
                serviceBreakdown.set(serviceCategory, currentCost + nodeCost);
              }
            }
          }
        }
      }
    }

    // Convert to array format for pie chart
    const result: ServiceCostBreakdown[] = Array.from(serviceBreakdown.entries())
      .map(([service, cost]) => ({
        service,
        cost,
        percentage: 0, // Will be calculated after sorting
      }))
      .sort((a, b) => b.cost - a.cost);

    // Calculate percentages based on service breakdown total
    const serviceTotalCost = result.reduce((sum, item) => sum + item.cost, 0);
    result.forEach((item) => {
      item.percentage = serviceTotalCost > 0 ? (item.cost / serviceTotalCost) * 100 : 0;
    });

    console.log(
      `Service breakdown total: $${serviceTotalCost.toFixed(2)}, Pipeline total: $${totalPipelineCost.toFixed(2)}`,
    );

    return {
      serviceBreakdown: result,
      totalCost: totalPipelineCost, // Use the pipeline-level total for consistency
    };
  }

  /**
   * Map component type to service category for cost aggregation
   */
  private mapComponentTypeToServiceCategory(componentType: string): string {
    if (componentType.startsWith('processor.lambda') || componentType.startsWith('processor.')) {
      return 'Lambda';
    } else if (componentType === 'buffer.sqs') {
      return 'SQS';
    } else if (componentType === 'buffer.kinesis') {
      return 'Kinesis';
    } else if (componentType === 'buffer.s3_firehose') {
      return 'Kinesis Firehose';
    } else if (componentType === 'storage.s3') {
      return 'S3';
    } else if (componentType === 'storage.dynamodb') {
      return 'DynamoDB';
    } else if (componentType === 'source.sns') {
      return 'SNS';
    } else {
      return 'Other';
    }
  }

  /**
   * Send a chat message to the AI Agent
   * Only available in gamma environment (backed by Bedrock in us-east-1)
   */
  async sendChatMessage(
    message: string,
    session: AuthSession | null,
    conversationHistory: any[] = [],
  ): Promise<{ response: string; conversation_history: any[]; error?: boolean }> {
    try {
      // Use a 65-second timeout for chat requests (API Gateway integration timeout is 60s)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 65000);

      try {
        const result = await this.makeRequest<any>('/chat', session, {
          method: 'POST',
          body: JSON.stringify({
            message,
            conversation_history: conversationHistory,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Handle proxy integration response (body is already parsed)
        if (typeof result === 'string') {
          const parsed = JSON.parse(result);
          return {
            response: parsed.response || 'No response received.',
            conversation_history: parsed.conversation_history || [],
            error: parsed.error,
          };
        }

        return {
          response: result.response || 'No response received.',
          conversation_history: result.conversation_history || [],
          error: result.error,
        };
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error('Error sending chat message:', error);
      const isGamma = this.getCurrentEnvironment() === 'gamma';
      return {
        response: isGamma
          ? `I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`
          : 'AI Assistant is only available in the gamma environment.',
        conversation_history: conversationHistory,
        error: true,
      };
    }
  }
}

// Export a singleton instance
export const apiService = new ApiService();
